import { Popper, TextField, Autocomplete } from "@mui/material";
import { ReactNode } from "react";
import LimitTag from "$components/LimitTag";

export type GenericSelectProps = {
  onBlur?: (e: any) => void;
  onChange: (selectedValueFromKey, selectedObject) => void;
  onInputChange?: (nextValue: string) => void;
  valueKey?: string;
  className?: string;
  options: any[];
  value: any;
  getOptionDisabled?: (opt: any) => boolean;
  getOptionLabel?: (opt: any) => string;
  error?: boolean;
  errorText?: string;
  limitTags?: number;
  label?: string;
  placeholder?: string;
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  style?: any;
  renderOption?: (opt: any) => ReactNode;
  filterOptions?: (opt: any) => any;
  filterSelectedOptions?: boolean;
  allowPopperFlip?: boolean;
  loading?: boolean;
  clearOnBlur?: boolean;
  blurOnSelect?: boolean;
  disableCloseOnSelect?: boolean;
  disableClearable?: boolean;
  noOptionsText?: string;
  inputValue?: string;
};

function GenericSelect({
  onBlur,
  onChange,
  onInputChange = () => {},
  options,
  value,
  valueKey = "value",
  label,
  placeholder,
  className,
  getOptionDisabled,
  getOptionLabel,
  filterOptions,
  error,
  errorText,
  multiple,
  required,
  disabled,
  style,
  filterSelectedOptions = false,
  limitTags = 2,
  allowPopperFlip = true,
  loading = false,
  noOptionsText = "Pas de résultat",
  clearOnBlur = true,
  blurOnSelect,
  disableCloseOnSelect,
  disableClearable,
  inputValue,
  renderOption,
  ...props
}: GenericSelectProps) {
  let safeOptions = options;
  if (!Array.isArray(safeOptions)) {
    console.error(
      "Options passed to the <GenericSelect /> was not an array:",
      options
    );
    safeOptions = [];
  }

  const getMatchedValue = () => {
    if (!value && value !== 0) {
      return multiple ? [] : null;
    }

    if (multiple) {
      if (!Array.isArray(value)) {
        // TODO: check with tristan if he changed eslint in pipeline
        // console.warn(`Multiple select expects array value, got:`, value);
        return [];
      }

      return safeOptions.filter((opt) => {
        const optValue = typeof opt === "object" ? opt[valueKey] : opt;
        return value.some((val) => {
          // Handle both string and number comparisons
          return String(val) === String(optValue) || val === optValue;
        });
      });
    }
    const foundOption = safeOptions.find((opt) => {
      const optValue = typeof opt === "object" ? opt[valueKey] : opt;
      // Handle both string and number comparisons
      return String(value) === String(optValue) || value === optValue;
    });
    return foundOption || null;
  };

  const val = getMatchedValue();

  const labelText = required && label?.slice(-1) !== "*" ? `${label} *` : label;

  // Default getOptionLabel function
  const defaultGetOptionLabel = (option: any) => {
    if (!option) return "";

    // Handle string options
    if (typeof option === "string") return option;

    // Handle object options - try common label properties
    return (
      option.name ||
      option.label ||
      option.title ||
      option[valueKey] ||
      "- No label defined -"
    );
  };

  // Enhanced renderOption function
  const enhancedRenderOption = (props: any, option: any) => {
    if (renderOption) {
      return (
        <li {...props} key={option[valueKey] || option}>
          {renderOption(option)}
        </li>
      );
    }

    return (
      <li {...props} key={option[valueKey] || option}>
        {(getOptionLabel || defaultGetOptionLabel)(option)}
      </li>
    );
  };

  // Enhanced isOptionEqualToValue function for better matching
  const isOptionEqualToValue = (option: any, value: any) => {
    if (!option || !value) return option === value;

    const optionValue = typeof option === "object" ? option[valueKey] : option;
    const compareValue = typeof value === "object" ? value[valueKey] : value;

    // Handle both strict equality and string comparison
    return (
      optionValue === compareValue ||
      String(optionValue) === String(compareValue)
    );
  };

  return (
    <div style={{ flex: 1 }}>
      <Autocomplete
        key={`${label}-${valueKey}`}
        value={val}
        inputValue={inputValue}
        options={safeOptions}
        style={style}
        loading={loading}
        clearOnBlur={clearOnBlur}
        blurOnSelect={blurOnSelect}
        multiple={multiple}
        onBlur={onBlur}
        filterOptions={filterOptions}
        getOptionDisabled={getOptionDisabled}
        disableListWrap
        disabled={disabled}
        disableClearable={disabled || disableClearable}
        disableCloseOnSelect={disableCloseOnSelect}
        noOptionsText={noOptionsText}
        filterSelectedOptions={filterSelectedOptions}
        className={className}
        limitTags={limitTags}
        getLimitTagsText={(more) => <LimitTag nbMore={more} />}
        isOptionEqualToValue={isOptionEqualToValue}
        onInputChange={(event, nextValue, reason) => {
          if (reason === "reset" && multiple) {
            // TODO: check with tristan if he changed eslint in pipeline
            // console.warn(
            //   `Warning: onInputChange was BLOCKED on a multiple GenericSelect (${label})... Otherwise it prevents the inputValue from being updated (nextValue:${nextValue}, reason:${reason})`
            // );
          } else {
            onInputChange(nextValue);
          }
        }}
        onChange={(e, nextSelected) => {
          if (Array.isArray(nextSelected)) {
            const selectedValues = nextSelected.map((opt) =>
              typeof opt === "object" ? opt[valueKey] : opt
            );
            onChange(selectedValues, nextSelected);
          } else {
            const selectedValue = nextSelected
              ? typeof nextSelected === "object"
                ? nextSelected[valueKey]
                : nextSelected
              : null;
            onChange(selectedValue, nextSelected);
          }
        }}
        getOptionLabel={getOptionLabel || defaultGetOptionLabel}
        renderOption={enhancedRenderOption}
        renderInput={(params: any) => (
          <TextField
            {...params}
            label={labelText}
            variant="outlined"
            error={error}
            helperText={errorText}
            placeholder={multiple && val?.length > 0 ? undefined : placeholder}
          />
        )}
        PopperComponent={(props) => (
          <Popper
            {...props}
            modifiers={[
              {
                name: "flip",
                enabled: allowPopperFlip,
              },
            ]}
            placement="bottom"
          />
        )}
        {...props}
      />
    </div>
  );
}

export default GenericSelect;
